import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCurrentUser } from '@/core/hooks/useCurrentUser';
import { CurrentUserModel } from '@/core/models/CurrentUserModel';
import { Storage } from '@/core/services/Storage';
import { Message, MessageAuthor, MessageByCopilot, MessageByUser } from './useCopilotCommunication.types';

export const storages = {
  map: new Storage('copilot-messages.map'),
  reports: new Storage('copilot-messages.reports'),
  'gi-actions': new Storage('copilot-messages.gi-actions'),
  default: new Storage('copilot-messages'),
};

const Key = {
  SessionKey: 'session_key',
  User: 'user',
  Copilot: 'copilot',
};

export type StorageKey = keyof typeof storages;
type RestoredMessage = Message & { restored?: boolean };
type StoredMapMassages = Map<RestoredMessage['messageId'], RestoredMessage>;

const getStorage = (storageKey: StorageKey) => storages[storageKey] ?? storages['default'];

export const getMessageId = (prefix = 'id') => `${prefix}-${Date.now()}`;

const mapRestoredMessages = (messages: Message[] = []): StoredMapMassages => {
  return new Map(messages.map((item) => [item.messageId, { ...item, restored: true }]));
};

const invokeStoredMessages = ({
  byAuthor,
  sessionKey,
  storageKey,
}: {
  byAuthor: (typeof Key)['User'] | (typeof Key)['Copilot'];
  sessionKey: string;
  storageKey: StorageKey;
}): StoredMapMassages => {
  const storage = getStorage(storageKey);
  const messages = storage.getValue<Message[]>(byAuthor);
  if (storage.getValue<string>(Key.SessionKey) === sessionKey) {
    return mapRestoredMessages(messages);
  }
  return new Map();
};

const getSessionKey = (user: CurrentUserModel) => `${user.session_id}_${user.org_id}`;

export const useMessagesStore = ({ storageKey }: { storageKey: StorageKey }) => {
  const { user } = useCurrentUser();
  const [userMessages, setUserMessages] = useState<StoredMapMassages>(
    invokeStoredMessages({ byAuthor: Key.User, sessionKey: getSessionKey(user), storageKey }),
  );
  const [copilotMessages, setCopilotMessages] = useState<StoredMapMassages>(
    invokeStoredMessages({ byAuthor: Key.Copilot, sessionKey: getSessionKey(user), storageKey }),
  );

  const lastUserMessage = useMemo(() => {
    return (
      Array.from(userMessages.values())
        .reverse()
        .find((item) => !item.restored) || null
    );
  }, [userMessages]);

  useEffect(() => {
    return () => {
      const storage = getStorage(storageKey);
      storage.setValue(Key.SessionKey, getSessionKey(user));
      // Persist as arrays to ensure JSON serialization works; Maps aren't JSON-serializable
      storage.setValue(Key.User, Array.from(userMessages.values()));
      storage.setValue(Key.Copilot, Array.from(copilotMessages.values()));
    };
  }, [userMessages, copilotMessages, user]);

  const addUserMessage = useCallback(
    ({
      message,
      messageId,
      selected_dates,
      metadata,
    }: {
      message: string;
      messageId: string;
      selected_dates?: MessageByUser['selected_dates'];
      metadata?: MessageByUser['metadata'];
    }) => {
      if (message) {
        setUserMessages((messages) => {
          const next = new Map(messages).set(messageId, {
            message,
            messageId,
            selected_dates,
            metadata,
            time: Date.now(),
            author: MessageAuthor.User,
          });

          return next;
        });
      }
    },
    [],
  );

  const addCopilotMessage = useCallback(
    ({
      message,
      messageId,
      citations,
      dateExtraction,
      subsetPath,
      responseType,
      metadata,
    }: {
      message: string;
      messageId: string;
      citations?: string;
      dateExtraction?: MessageByCopilot['dateExtraction'];
      subsetPath?: string;
      responseType: MessageByCopilot['responseType'];
      metadata?: MessageByCopilot['metadata'];
    }) => {
      // Allow empty messages if dateExtraction is present (for confirmation UI)
      if (!message && !dateExtraction) return;
      setCopilotMessages((messages) => {
        const next = new Map(messages);
        const existed = next.get(messageId);
        next.set(messageId, {
          message,
          messageId,
          citations,
          dateExtraction,
          subsetPath,
          responseType,
          metadata,
          time: existed?.time ?? Date.now(),
          author: MessageAuthor.Copilot,
        });
        return next;
      });
    },
    [],
  );

  return {
    userMessages,
    copilotMessages,
    lastUserMessage,
    addUserMessage,
    addCopilotMessage,
  };
};
